import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Play, Square, Loader2, Gauge, Trophy, AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "../ui/button";
import { Slider } from "../ui/slider";
import { NavGrid } from "./NavGrid";
import { NavFuelBar, NavGoalCounter, NavDrillStatus, NavGemBadge } from "./NavHud";
import { toast } from "sonner";
import { GEM_BONUS } from "./navShared";
import { DIR_DEG, rotateTowards, worldMeta, key } from "./navShared";

const BASE_STEP_MS = 480;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const startState = (nav) => ({ x: nav.start.x, y: nav.start.y, deg: DIR_DEG[nav.start.dir] || 0, hidden: false, ms: 0, active: false, scanning: false });
const BAD = new Set(["danger", "error", "out_of_fuel"]);

// Grid + Play / speed / Stop. `runProgram()` must resolve to the server's nav result ({steps, outcome, message, ...}).
// ref.playResult(result) animates a result you already have (used after Submit); ref.stop() resets to coding mode.
export const NavPlayback = forwardRef(({ nav, runProgram, disabled, disabledReason, onRunStart, onRunEnd, compact = false }, ref) => {
  const world = worldMeta(nav);
  const [drill, setDrill] = useState(() => startState(nav));
  const [status, setStatus] = useState("idle"); // idle | loading | playing | done
  const [result, setResult] = useState(null);
  const [flash, setFlash] = useState(null);
  const [collected, setCollected] = useState(() => new Set());
  const [broken, setBroken] = useState(() => new Set());
  const [breaking, setBreaking] = useState(null);
  const [charges, setCharges] = useState(0);
  const [gem, setGem] = useState(null);
  const [gems, setGems] = useState([]);
  const [fuel, setFuel] = useState(nav.fuel ?? null);
  const [speed, setSpeed] = useState(1);
  const speedRef = useRef(1);
  const runIdRef = useRef(0);
  const breakingRef = useRef(null);
  speedRef.current = speed;

  const reset = useCallback(() => {
    setDrill(startState(nav));
    setFlash(null);
    setCollected(new Set());
    setBroken(new Set());
    setBreaking(null);
    setCharges(0);
    setGem(null);
    setGems([]);
    setFuel(nav.fuel ?? null);
  }, [nav]);

  useEffect(() => { reset(); setStatus("idle"); setResult(null); runIdRef.current += 1; }, [JSON.stringify(nav)]); // eslint-disable-line react-hooks/exhaustive-deps

  const stop = useCallback(() => {
    runIdRef.current += 1;
    reset();
    setStatus("idle");
    setResult(null);
  }, [reset]);

  const animate = useCallback(async (res) => {
    const myRun = ++runIdRef.current;
    const alive = () => runIdRef.current === myRun;
    setResult(res);
    reset();
    setGems((res.gems_found_before || []).map((g) => ({ x: g[0], y: g[1] }))); // gems this player already revealed on earlier runs
    setStatus("playing");
    onRunStart?.();
    let deg = DIR_DEG[nav.start.dir] || 0;
    const pickUp = (step) => setCollected((c) => new Set([...c, key(step.x, step.y)]));
    const blink = async (kind, ms) => {
      setFlash(kind);
      await sleep(ms);
      if (alive()) setFlash(null);
    };
    await sleep(150);
    for (const step of res.steps || []) {
      if (!alive()) return;
      const ms = Math.max(60, BASE_STEP_MS / speedRef.current);
      if (step.f != null) setFuel(step.f);
      const setActive = () => { if (step.c != null) { setCharges(step.c); setDrill((d) => ({ ...d, active: step.c > 0 })); } };
      setDrill((d) => (d.scanning === !!step.s ? d : { ...d, scanning: !!step.s }));
      if (step.a !== "F" && step.a !== "B") setActive();
      if (step.a === "F" || step.a === "B") {
        setDrill((d) => ({ ...d, x: step.x, y: step.y, ms }));
        await sleep(ms);
        if (!alive()) return;
        setActive(); // the drill switches off only once it has bored into the rock
        if (breakingRef.current) { setBroken((b) => new Set([...b, breakingRef.current])); setBreaking(null); breakingRef.current = null; }
      } else if (step.a === "drill") {
        // rock crumbles while the next F step drives the drill into its cell
        breakingRef.current = key(step.x, step.y);
        setBreaking(breakingRef.current);
        await sleep(ms * 0.25);
      } else if (step.a === "power") {
        pickUp(step);
        await blink("power", ms * 0.8);
      } else if (step.a === "gem") {
        // the scanner revealed a hidden gem next to the drill
        setGem({ x: step.x, y: step.y, key: Date.now() });
        setGems((g) => (g.some((c) => (c.x ?? c[0]) === step.x && (c.y ?? c[1]) === step.y) ? g : [...g, { x: step.x, y: step.y }]));
        if (step.new !== false) toast.success(`Hidden gem found! +${GEM_BONUS} when your solution is graded`, { duration: 4000 });
        await sleep(Math.max(ms * 2.5, 900));
        if (!alive()) return;
        setGem(null);
      } else if (step.a === "L" || step.a === "R") {
        deg = rotateTowards(deg, step.d);
        setDrill((d) => ({ ...d, deg, ms: ms * 0.7 }));
        await sleep(ms * 0.7);
      } else if (step.a === "bump") {
        await blink("bump", ms * 0.5);
        await sleep(ms * 0.2);
      } else if (step.a === "tunnel") {
        setDrill((d) => ({ ...d, hidden: true, ms: 0 }));
        await sleep(ms * 0.45);
        if (!alive()) return;
        setDrill((d) => ({ ...d, x: step.x, y: step.y, hidden: false, ms: 0 }));
        await sleep(ms * 0.45);
      } else if (step.a === "danger") {
        setFlash("danger");
        await sleep(ms);
      } else if (step.a === "crack") {
        await blink("crack", ms * 0.6);
      } else if (step.a === "fuel") {
        pickUp(step);
        await blink("fuel", ms * 0.8);
      } else if (step.a === "collect") {
        pickUp(step);
        await blink("treasure", ms * 0.8);
      } else if (step.a === "treasure") {
        setFlash("treasure");
        await sleep(ms);
        pickUp(step);
      } else if (step.a === "out_of_fuel") {
        setFuel(0);
        setFlash("out_of_fuel");
        await sleep(ms);
      } else if (step.a === "stop") {
        await sleep(ms * 0.3);
      }
    }
    if (!alive()) return;
    setStatus("done");
    onRunEnd?.(res);
  }, [nav, reset, onRunStart, onRunEnd]);

  const play = useCallback(async () => {
    if (disabled || status === "loading" || status === "playing") return;
    setStatus("loading");
    setResult(null);
    try {
      const res = await runProgram();
      if (!res) { setStatus("idle"); return; }
      if (res.nav_rule_error || res.outcome === "rules") {
        setResult(res);
        setStatus("done");
        return;
      }
      await animate(res);
    } catch (e) {
      setResult({ outcome: "error", message: "Could not run your program. Please try again.", error: e.message });
      setStatus("done");
    }
  }, [disabled, status, runProgram, animate]);

  useImperativeHandle(ref, () => ({ playResult: animate, stop }), [animate, stop]);

  const tone = result?.reached ? "text-emerald-600 dark:text-emerald-400" : BAD.has(result?.outcome) ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400";

  return (
    <div className="space-y-3" data-testid="nav-playback" data-status={status} data-world={world.id}>
      {world.fuel && <NavFuelBar nav={nav} fuel={fuel} flash={flash} />}
      <NavGrid nav={nav} drill={drill} flash={flash} collected={[...collected]} broken={[...broken]} breaking={breaking} gem={gem} gems={gems}>
        <NavGoalCounter nav={nav} collected={collected} />
        <NavDrillStatus nav={nav} charges={charges} />
        <NavGemBadge count={gems.length} scanning={drill.scanning} />
      </NavGrid>
      <div className={`flex flex-wrap items-center gap-2 ${compact ? "" : "sm:gap-3"}`}>
        {status === "idle" || status === "done" ? (
          <Button onClick={play} disabled={disabled} className="gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="nav-play-btn" title={disabledReason || "Run your program"}>
            <Play className="w-4 h-4" /> {status === "done" ? "Play again" : "Play"}
          </Button>
        ) : (
          <Button disabled className="gap-1.5 rounded-full" data-testid="nav-playing-btn">
            <Loader2 className="w-4 h-4 animate-spin" /> {status === "loading" ? "Starting…" : "Running…"}
          </Button>
        )}
        <Button variant="outline" onClick={stop} disabled={status === "idle"} className="gap-1.5 rounded-full" data-testid="nav-stop-btn" title="Stop and go back to coding">
          {status === "done" ? <RotateCcw className="w-4 h-4" /> : <Square className="w-4 h-4" />} {status === "done" ? "Reset" : "Stop"}
        </Button>
        <div className="flex items-center gap-2 flex-1 min-w-[160px] max-w-xs" data-testid="nav-speed">
          <Gauge className="w-4 h-4 text-muted-foreground" />
          <Slider value={[speed]} min={0.5} max={4} step={0.5} onValueChange={([v]) => setSpeed(v)} className="flex-1" data-testid="nav-speed-slider" />
          <span className="text-xs font-semibold tabular-nums w-9 text-right" data-testid="nav-speed-value">{speed}×</span>
        </div>
      </div>
      {disabled && disabledReason && <p className="text-xs text-amber-600 dark:text-amber-400" data-testid="nav-disabled-reason">{disabledReason}</p>}
      {status === "done" && result && (
        <div className={`flex items-start gap-2 text-sm font-medium ${tone}`} data-testid="nav-result" data-outcome={result.outcome}>
          {result.reached ? <Trophy className="w-4 h-4 mt-0.5" /> : <AlertTriangle className="w-4 h-4 mt-0.5" />}
          <span>
            {result.message}
            {result.error && <span className="block text-xs font-normal opacity-80 mt-0.5">{result.error}</span>}
            {result.outcome === "incomplete" && result.moves != null && <span className="block text-xs font-normal opacity-80 mt-0.5">{result.moves} move{result.moves === 1 ? "" : "s"} made.</span>}
          </span>
        </div>
      )}
    </div>
  );
});
